import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { languages } from '../data/translations';

const FlagDisplay = ({ lang, size = 'md' }) => {
  const sizeClass = size === 'lg' ? 'w-7 h-5' : 'w-6 h-4';
  
  if (lang.flagImage) {
    return (
      <img 
        src={lang.flagImage} 
        alt={lang.name}
        className={`${sizeClass} object-contain rounded-sm`}
      />
    );
  }
  return <span className={size === 'lg' ? 'text-lg' : 'text-base'}>{lang.flag}</span>;
};

const LanguageSelector = ({ isScrolled = true }) => {
  const { currentLang, changeLang } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentLanguage = languages.find(l => l.code === currentLang) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="language-selector-btn"
        className={`flex items-center gap-2 px-3 py-2 rounded-full transition-colors duration-300 ${
          isScrolled 
            ? 'bg-[#0d4023]/5 hover:bg-[#0d4023]/10 text-[#0d4023]' 
            : 'bg-white/10 hover:bg-white/20 text-white'
        }`}
      >
        <Globe className="w-4 h-4" />
        <FlagDisplay lang={currentLanguage} />
        <span className="text-sm font-medium hidden sm:inline">{currentLanguage.name}</span>
        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full mt-2 end-0 bg-white rounded-xl shadow-2xl border border-[#b89759]/10 overflow-hidden z-50 min-w-[200px]">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                changeLang(lang.code);
                setIsOpen(false);
              }}
              data-testid={`lang-option-${lang.code}`}
              className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-[#0d4023]/5 transition-colors duration-200 ${
                currentLang === lang.code ? 'bg-[#0d4023]/10' : ''
              }`}
            >
              <FlagDisplay lang={lang} size="lg" />
              <span className={`font-medium flex-1 text-start ${currentLang === lang.code ? 'text-[#0d4023]' : 'text-[#5a7062]'}`}>
                {lang.name}
              </span>
              {currentLang === lang.code && (
                <span className="text-[#b89759] text-lg">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
